/**
 * サーバが宣言した再生位置に、自分の YouTube プレイヤーを寄せ続ける。
 *
 * サーバは音声を中継せず「この時刻に何秒目だった」という宣言だけを配る。
 * 各クライアントはサーバ時刻から目標位置を自分で計算して合わせるので、
 * 同室でも遠距離でも同じ仕組みで動く。
 */

/** これ以上ずれたら強制シーク。滑らかに直すのを諦める境界 */
export const HARD_SYNC_THRESHOLD_SEC = 1.5;
/** これ以上ずれたら再生速度で寄せる。これ未満は放置する */
export const SOFT_SYNC_THRESHOLD_SEC = 0.3;
const NUDGE_RATE = 0.03;
const LOOP_INTERVAL_MS = 1000;
/** 読み込みやシークの直後は位置が安定しないので補正を控える */
const SETTLE_MS = 1500;

/**
 * iframe を作るときの実サイズ。CSS 側の .stage-frame iframe と必ず揃える。
 * 見た目はここから transform: scale() で縮めるので、レイアウトには影響しない。
 */
export const PLAYER_WIDTH = 1280;
export const PLAYER_HEIGHT = 720;

export type PlaybackTarget = {
  trackId: string;
  videoId: string;
  targetSec: number;
  paused: boolean;
};

export type SyncedPlayerOptions = {
  container: HTMLElement;
  getTarget: () => PlaybackTarget | null;
  /**
   * タイミング補正をするかどうか。毎 tick 最新の値を読む。
   *
   * 音を出している端末が1台だけなら合わせる相手がいないので false にする。
   * 補正のための seekTo は音飛びを起こし、setPlaybackRate による 0.97/1.03 倍の
   * 速度変更は音の揺れとして聞こえるため、不要な場面では一切かけない。
   */
  shouldCorrect: () => boolean;
  /** 補正を切っているとき、実際の再生位置をサーバへ返すためのコールバック */
  onPosition: (trackId: string, positionMs: number) => void;
  /**
   * IFrame Player から読めた実測のメタデータ。曲の長さはサーバの曲送りタイマーの
   * 精度を決め、タイトルはリンクからの取得に失敗した曲の表示を修復する。
   */
  onMeta: (meta: {
    trackId: string;
    durationMs: number;
    title?: string;
    artist?: string;
  }) => void;
  onEnded: (trackId: string) => void;
  /** 埋め込み禁止や地域制限で再生できない場合。キューを止めないため次へ送る */
  onUnplayable: (trackId: string, code: number) => void;
  onDriftChange?: (driftSec: number) => void;
};

type YTPlayer = {
  loadVideoById(opts: { videoId: string; startSeconds?: number }): void;
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  getVideoData(): { title?: string; author?: string } | undefined;
  setPlaybackRate(rate: number): void;
  destroy(): void;
};

type YTNamespace = {
  Player: new (el: HTMLElement | string, config: unknown) => YTPlayer;
  PlayerState: { ENDED: number; PLAYING: number; PAUSED: number; BUFFERING: number };
};

declare global {
  interface Window {
    YT?: YTNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const STATE_ENDED = 0;
const STATE_PLAYING = 1;
const STATE_BUFFERING = 3;

let apiPromise: Promise<YTNamespace> | null = null;

function loadIframeApi(): Promise<YTNamespace> {
  if (apiPromise) return apiPromise;
  apiPromise = new Promise((resolve, reject) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
      else reject(new Error('YouTube IFrame API の初期化に失敗しました'));
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => reject(new Error('YouTube IFrame API を読み込めませんでした'));
    document.head.appendChild(script);
  });
  return apiPromise;
}

export class SyncedPlayer {
  private player: YTPlayer | null = null;
  private loopTimer: number | null = null;
  private loadedTrackId: string | null = null;
  private settleUntilMs = 0;
  private reportedMetaFor: string | null = null;
  private currentRate = 1;
  private disposed = false;

  private constructor(private readonly opts: SyncedPlayerOptions) {}

  /**
   * プレイヤーを作る。ブラウザの自動再生制限があるため、必ずユーザーの
   * 操作（「音を出す」トグル）を起点に呼ぶ必要がある。
   */
  static async create(opts: SyncedPlayerOptions): Promise<SyncedPlayer> {
    const instance = new SyncedPlayer(opts);
    const YT = await loadIframeApi();
    if (instance.disposed) return instance;

    const mount = document.createElement('div');
    opts.container.appendChild(mount);

    const initial = opts.getTarget();
    // videoId のキーを undefined で渡すと IFrame API が "Invalid video id" を
    // 投げて起動そのものが失敗する。キューが空のうちはキー自体を省き、
    // 曲が入った時点で loadVideoById で読み込む。
    const config: Record<string, unknown> = {
      // YouTube はプレイヤーの実サイズを見て配信品質を決め、音声のビットレートも
      // それに引きずられる。小さい枠のままだと音がはっきり悪くなるので、
      // iframe 自体は 1280x720 で作り、見た目は CSS の transform で縮める。
      width: PLAYER_WIDTH,
      height: PLAYER_HEIGHT,
      playerVars: {
        autoplay: 1,
        controls: 0,
        disablekb: 1,
        modestbranding: 1,
        playsinline: 1,
        rel: 0,
        iv_load_policy: 3,
        ...(initial ? { start: Math.floor(initial.targetSec) } : {}),
      },
      events: {
        onReady: () => {
          if (instance.disposed) return;
          instance.loadedTrackId = initial?.trackId ?? null;
          instance.settleUntilMs = Date.now() + SETTLE_MS;
          instance.reportMetaIfReady();
          instance.tick();
        },
        onStateChange: (event: { data: number }) => {
          if (instance.disposed) return;
          if (event.data === STATE_ENDED) {
            const trackId = instance.loadedTrackId;
            if (trackId) instance.opts.onEnded(trackId);
            return;
          }
          if (event.data === STATE_PLAYING) instance.reportMetaIfReady();
        },
        onError: (event: { data: number }) => {
          if (instance.disposed) return;
          const trackId = instance.loadedTrackId;
          if (trackId) instance.opts.onUnplayable(trackId, event.data);
        },
      },
    };
    if (initial) config['videoId'] = initial.videoId;

    instance.player = new YT.Player(mount, config);

    instance.loopTimer = window.setInterval(() => instance.tick(), LOOP_INTERVAL_MS);
    return instance;
  }

  /**
   * 1秒ごとに目標位置との差を見て寄せる。
   *
   *   1.5秒以上   → seekTo で強制同期
   *   0.3〜1.5秒  → 再生速度 0.97 / 1.03 で滑らかに寄せる
   *   0.3秒未満   → 何もしない
   *
   * 遠距離では各自が自分のスピーカーで聴くため ±300ms のずれは知覚できない。
   * 同室で複数端末から音を出すと数十msでも不快なので、その場合は
   * 「音を出す」端末を1台に絞る運用で回避する。
   */
  private tick(): void {
    const player = this.player;
    if (!player || this.disposed) return;

    const target = this.opts.getTarget();
    if (!target) {
      safe(() => player.pauseVideo());
      return;
    }

    if (target.trackId !== this.loadedTrackId) {
      this.loadedTrackId = target.trackId;
      this.reportedMetaFor = null;
      this.settleUntilMs = Date.now() + SETTLE_MS;
      this.setRate(1);
      safe(() =>
        player.loadVideoById({
          videoId: target.videoId,
          startSeconds: Math.max(0, target.targetSec),
        }),
      );
      if (target.paused) safe(() => player.pauseVideo());
      return;
    }

    if (target.paused) {
      safe(() => player.pauseVideo());
      this.setRate(1);
      return;
    }

    const state = safe(() => player.getPlayerState()) ?? -1;
    if (state !== STATE_PLAYING && state !== STATE_BUFFERING) {
      safe(() => player.playVideo());
    }

    this.reportMetaIfReady();

    // バッファリング中や読み込み直後は現在位置が信用できない
    if (state === STATE_BUFFERING || Date.now() < this.settleUntilMs) return;

    const actual = safe(() => player.getCurrentTime()) ?? 0;

    // 合わせる相手がいないなら、この端末が基準。何も補正せずに素直に流し、
    // 実位置だけサーバへ返して他の人の進捗バーと曲送りを合わせる。
    if (!this.opts.shouldCorrect()) {
      this.setRate(1);
      this.opts.onDriftChange?.(0);
      this.opts.onPosition(target.trackId, Math.round(actual * 1000));
      return;
    }

    const drift = target.targetSec - actual;
    this.opts.onDriftChange?.(drift);

    if (Math.abs(drift) >= HARD_SYNC_THRESHOLD_SEC) {
      this.settleUntilMs = Date.now() + SETTLE_MS;
      this.setRate(1);
      safe(() => player.seekTo(Math.max(0, target.targetSec), true));
      return;
    }
    if (Math.abs(drift) >= SOFT_SYNC_THRESHOLD_SEC) {
      this.setRate(drift > 0 ? 1 + NUDGE_RATE : 1 - NUDGE_RATE);
      return;
    }
    this.setRate(1);
  }

  private setRate(rate: number): void {
    if (Math.abs(this.currentRate - rate) < 0.001) return;
    this.currentRate = rate;
    safe(() => this.player?.setPlaybackRate(rate));
  }

  /**
   * 検索結果の尺は当てにならず、リンクからの取得も一時的に失敗する。
   * IFrame Player が知っている実測値をサーバに返して、曲送りのタイマーを
   * 正確にし、暫定タイトルのままの曲を修復する。
   */
  private reportMetaIfReady(): void {
    const trackId = this.loadedTrackId;
    if (!trackId || this.reportedMetaFor === trackId) return;
    const seconds = safe(() => this.player?.getDuration() ?? 0) ?? 0;
    if (seconds <= 0) return;
    this.reportedMetaFor = trackId;
    const data = safe(() => this.player?.getVideoData());
    this.opts.onMeta({
      trackId,
      durationMs: Math.round(seconds * 1000),
      title: data?.title,
      artist: data?.author,
    });
  }

  destroy(): void {
    this.disposed = true;
    if (this.loopTimer !== null) window.clearInterval(this.loopTimer);
    this.loopTimer = null;
    safe(() => this.player?.destroy());
    this.player = null;
    this.opts.container.replaceChildren();
  }
}

/** IFrame API はプレイヤーの状態次第でメソッドが未定義になることがある */
function safe<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}
